import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-client';
import type { WebTask } from './tasks';

export type { SentOutreachSummary } from './outreach-rows';

/**
 * What a task's outreaches came to: who they went to, over what, and whether anyone wrote back.
 *
 * Read out of the ALREADY-LOADED task list rather than fetched. The fold onto each row is the client
 * merge's (`lib/outreaches.ts#mergeOutreaches`) — one outreach read for the whole list — so a detail
 * opened off the Tasks list pays nothing at all for this section.
 *
 * ⚠️ One consequence of the split: the fold arrives with the outreach read, a beat after the list. A
 * detail opened in that window falls through to the route's own read below — which is what that fallback
 * is for, so the section is never MISSING, only ever sourced differently.
 *
 * Scans every task-list scope (`tasksAllScopes`), because the detail can be opened from the
 * account-wide list or from a Space-scoped one and the row lives in whichever of those was fetched.
 */
export function useSentOutreaches(taskId: string): WebTask['sentOutreaches'] {
  const queryClient = useQueryClient();
  if (!taskId) return undefined;
  const scopes = queryClient.getQueriesData<WebTask[]>({ queryKey: queryKeys.tasksAllScopes });
  for (const [, tasks] of scopes) {
    const row = tasks?.find((t) => t.id === taskId);
    if (row?.sentOutreaches?.length) return row.sentOutreaches;
  }
  return undefined;
}
